/**
 * Builds the queue processor responsible for dispatching WS download requests.
 * @param {Object} deps
 * @returns {Function}
 */
const createProcessDownloadQueue = ({
  getSocket,
  webSocketOpenState,
  parseQueue,
  maxParseQueue,
  getInFlightDownloads,
  setInFlightDownloads,
  maxInFlightDownloads,
  dm,
  seriesTransportModes,
  DOWNLOAD_TRANSPORT_MODE,
  getDownloadBatchRequestMaxItems,
  sendMessage,
  setSeriesItemInFlight,
  seriesStateTrackers,
  downloadPerf,
  pendingChunks
}) => {
  /**
   * Pops scheduler slots and emits WS download requests while windows allow it.
   * @returns {void}
   */
  return function processDownloadQueue() {
    // Stop immediately when the transport is unavailable or parse backpressure is active.
    const socket = getSocket();
    if (!socket || socket.readyState !== webSocketOpenState) return;
    if (parseQueue.length >= maxParseQueue) {
      return;
    }

    while (getInFlightDownloads() < maxInFlightDownloads) {
      const nextSlot = dm.getNextSlot(1);
      const slot = Array.isArray(nextSlot) ? nextSlot.slice().pop() : null;

      if (!slot) {
        return;
      }

      const transportMode =
        seriesTransportModes[slot.seriesId] || DOWNLOAD_TRANSPORT_MODE.AUTO;
      const maxBatchItemsPerRequest = getDownloadBatchRequestMaxItems();
      const remainingCapacity = maxInFlightDownloads - getInFlightDownloads();
      // Batch mode aggregates as many pending slots as the window can safely accept.
      const maxSlotsForRequest =
        transportMode === DOWNLOAD_TRANSPORT_MODE.BATCH
          ? Math.max(1, Math.min(maxBatchItemsPerRequest, remainingCapacity))
          : 1;
      let slots = [slot];
      if (maxSlotsForRequest > 1) {
        const extraSlotsRaw = dm.getNextSlot(maxSlotsForRequest - 1);
        const extraSlots = Array.isArray(extraSlotsRaw)
          ? extraSlotsRaw.filter(Boolean)
          : [];
        if (extraSlots.length > 0) {
          slots = slots.concat(extraSlots);
        }
      }

      const wsMessage = {
        data: {
          images: slots.map(current => current.imageId),
          elementIds: slots.map(current => current.studyId),
          transport: transportMode
        }
      };

      try {
        sendMessage(JSON.stringify(wsMessage));
        slots.forEach(current => {
          setInFlightDownloads(getInFlightDownloads() + 1);
          setSeriesItemInFlight(
            seriesStateTrackers[current.seriesId],
            current.imageId,
            true
          );
          downloadPerf.markRequestSent({
            seriesId: current.seriesId,
            dbImageId: current.imageId,
            elementId: current.studyId,
            inFlight: getInFlightDownloads(),
            parseQueueLength: parseQueue.length,
            pendingChunkCount: Object.keys(pendingChunks).length
          });
        });
      } catch (sendError) {
        console.error("Failed to send series download message", sendError);
        downloadPerf.recordError({
          seriesId: slot.seriesId,
          dbImageId: slot.imageId,
          stage: "request_send",
          message: sendError?.message || sendError
        });
        return;
      }
    }
  };
};

export { createProcessDownloadQueue };
